import { Spinner } from '@gmg/gmg-react-components';
import { FunctionComponent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGetLicenseSystemAccessByUserId } from 'src/graphql/useGetLicenseSystemAccessByUserId';
import { AddOrEditUserModalForm } from '../AddOrEditUserModal';
import CpGoSettingsModal from './CpGoSettingsModal';
import { DEFAULT_ROLE_SELECTION } from './cpGo';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';

export interface CpGoSettingsModalContainerProps {
    onCloseModal: () => void;
    userId?: string;
    availableLicenseSystems: Array<LicenseSystem>;
}

const CpGoSettingsModalContainer: FunctionComponent<CpGoSettingsModalContainerProps> = (props) => {

    const { data: licenseSystemAccessData, isLoading } = useGetLicenseSystemAccessByUserId(props.userId, 'ColorProofGo');
    const { setValue, getValues } = useFormContext<AddOrEditUserModalForm>();

    const appliedCpGoAssignedLicenseSystems = getValues('cpGoAssignedLicenseSystems');
    const appliedCpGoRole = getValues('cpGoRoleSelection');
    const hasValidSettings = !!appliedCpGoAssignedLicenseSystems || !!appliedCpGoRole;
    const shouldSkipDefaultValueSetup = hasValidSettings || isLoading;

    useEffect(() => {
        // Here we set the defaultValues the very first time the cpGoSettings modal displayed
        // and after that we use the values from react-hook-form's state via useFormContext
        if (shouldSkipDefaultValueSetup) return;

        const pairedLicenseSystemIds = licenseSystemAccessData?.map((lsData) => lsData.licenseSystemId) || [];
        const hasPairedLicenseSystems = pairedLicenseSystemIds.length > 0;

        const licenseSystemsBelongingToOrg = props.availableLicenseSystems.filter((ls) => pairedLicenseSystemIds.includes(ls.id));
        const licenseSystemsNotBelongingToOrg = pairedLicenseSystemIds.filter((lsId) => !props.availableLicenseSystems.some(ls => ls.id === lsId));

        const defaultLicenseSystems = hasPairedLicenseSystems
            ? [
                ...licenseSystemsBelongingToOrg.map(({ id, computerName, location }) => ({ value: id, label: `${id}: ${computerName} — ${location}` })),
                ...licenseSystemsNotBelongingToOrg.map((id) => ({ value: id, label: id })),
            ]
            : undefined;

        const defaultRole = hasPairedLicenseSystems
            ? { value: licenseSystemAccessData![0].role, label: licenseSystemAccessData![0].role }
            : DEFAULT_ROLE_SELECTION;

        setValue('cpGoRoleSelection', defaultRole);
        setValue('cpGoAssignedLicenseSystems', defaultLicenseSystems);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [licenseSystemAccessData]);


    if (isLoading) {
        return <Spinner />;
    }

    const handleCancelButtonClick = () => {
        setValue('cpGoRoleSelection', appliedCpGoRole);
        setValue('cpGoAssignedLicenseSystems', appliedCpGoAssignedLicenseSystems);
        props.onCloseModal();
    };

    return (
        <CpGoSettingsModal
            onCloseModal={handleCancelButtonClick}
            onApplyButtonClick={props.onCloseModal}
            allAvailableLicenseSystemOptions={
                props.availableLicenseSystems
                    .filter(ls => ls.cpGo.isLicensed)
                    .map(({ id, computerName, location }) => (
                        { value: id, label: `${id}: ${computerName} — ${location}` }),
                    )}
            shownDefaultCpGoRole={appliedCpGoRole}
            alreadyPairedLicenseSystems={appliedCpGoAssignedLicenseSystems}
        />
    );
};

export default CpGoSettingsModalContainer;